import { FilterModalType } from '@/components/filters/types'
import { trackEvent } from '@/tracking'

/**
 * Sub-feature IDs for ZQA Applications team tracking events.
 *
 * @see https://docs.google.com/spreadsheets/d/1MR3ta7lpKB7EBQp3p2-UFGTlHZ7yM3TqEK0a4JIknWY
 */
export const APPLICATIONS_SUB_FEATURE_IDS = {
  REVIEW_ASSIGNMENTS: 590,
  REVIEW_ASSIGNMENTS_REPORTING: 93,
  CALIBRATION_SESSIONS: 708,
  CALIBRATION_DASHBOARD: 1126,
  FILTERS: 1369,
  PINS: 1030,
  QUIZZES: 398,
  COACHING_SESSIONS: 1539,
  COACHING_DASHBOARD: 955,
} as const

export type ApplicationsSubFeatureId =
  (typeof APPLICATIONS_SUB_FEATURE_IDS)[keyof typeof APPLICATIONS_SUB_FEATURE_IDS]

/**
 * Event names for ZQA Applications team tracking (ZQAAP-652).
 *
 * Kebab-case `feature-object-action`. Variant dimensions (e.g. which review button, filter
 * context) are passed as the standard `entityName` field on `trackEvent`, NOT baked into the
 * event name — this keeps names stable and the data groupable in Snowflake without approval.
 */
export const APPLICATIONS_EVENT_NAMES = {
  // Creating an assignment. REVIEW_ASSIGNMENTS.
  ASSIGNMENT_TYPE_SELECTED: 'assignment-type-selected',
  ASSIGNMENT_REVIEWEE_CLASS_CHANGED: 'assignment-reviewee-class-changed',
  ASSIGNMENT_REVIEWEE_TYPE_SELECTED: 'assignment-reviewee-type-selected',
  ASSIGNMENT_CONDITION_ADDED: 'assignment-condition-added',
  ASSIGNMENT_CONDITION_REMOVED: 'assignment-condition-removed',
  ASSIGNMENT_GOAL_OPTION_TOGGLED: 'assignment-goal-option-toggled',
  ASSIGNMENT_MATCHING_COUNT_REQUESTED: 'assignment-matching-count-requested',
  ASSIGNMENT_MATCHING_COUNT_LOADED: 'assignment-matching-count-loaded',
  ASSIGNMENT_MATCHING_COUNT_FAILED: 'assignment-matching-count-failed',
  ASSIGNMENT_CREATE_CLICKED: 'assignment-create-clicked',
  // Editing an assignment. REVIEW_ASSIGNMENTS.
  ASSIGNMENT_PUBLISH_CHANGES: 'assignment-publish-changes',
  // Assignment page (tasks consumption). REVIEW_ASSIGNMENTS.
  ASSIGNMENT_INCLUDE_COMPLETED_TOGGLED: 'assignment-include-completed-toggled',
  ASSIGNMENT_TASK_REASSIGN_CLICKED: 'assignment-task-reassign-clicked',
  ASSIGNMENT_TASK_REPLACE_CLICKED: 'assignment-task-replace-clicked',
  ASSIGNMENT_TASK_REMOVE_CLICKED: 'assignment-task-remove-clicked',
  REVIEW_STARTED: 'review-started',
  ASSIGNMENT_INFO_PANEL_OPENED: 'assignment-info-panel-opened',
  // Assignment details (info panel). REVIEW_ASSIGNMENTS.
  ASSIGNMENT_DETAILS_REASSIGN_CLICKED: 'assignment-details-reassign-clicked',
  ASSIGNMENT_DETAILS_EDIT_CLICKED: 'assignment-details-edit-clicked',
  // ReviewerQA. REVIEW_ASSIGNMENTS.
  REVIEWERQA_EVALUATION_CANCELLED: 'reviewerqa-evaluation-cancelled',
  REVIEWERQA_EVALUATION_CLOSED_AFTER_EVAL: 'reviewerqa-evaluation-closed-after-eval',
  REVIEWERQA_NEXT_REVIEWER_CLICKED: 'reviewerqa-next-reviewer-clicked',
  // Filtering. FILTERS. Context (assignments vs conversations) goes in entityName.
  FILTER_CONDITION_ADDED: 'filter-condition-added',
  FILTER_SAVED: 'filter-saved',
  FILTER_TURN_INTO_ASSIGNMENT: 'filter-turn-into-assignment',
  FILTER_LEARN_ABOUT_FILTERING_CLICKED: 'filter-learn-about-filtering-clicked',
  // Calibrations. CALIBRATION_SESSIONS.
  CALIBRATION_SESSION_CREATE_CLICKED: 'calibration-session-create-clicked',
  CALIBRATION_SESSION_CREATED: 'calibration-session-created',
  CALIBRATION_SESSION_EDITED: 'calibration-session-edited',
  CALIBRATION_SESSION_OPENED: 'calibration-session-opened',
} as const

export type ApplicationsEventName =
  (typeof APPLICATIONS_EVENT_NAMES)[keyof typeof APPLICATIONS_EVENT_NAMES]

/** Where a review was started from — passed as `entityName` on the shared `review-started` event. */
export type ReviewStartSource = 'header' | 'panel-icon' | 'feedback-section'

/**
 * Track "start review" from any of its three entry points as one comparable event, with the
 * source in the standard `entityName` field (groupable via `GROUP BY entity_name`).
 */
export function trackReviewStarted(source: ReviewStartSource): void {
  trackEvent(APPLICATIONS_EVENT_NAMES.REVIEW_STARTED, APPLICATIONS_SUB_FEATURE_IDS.REVIEW_ASSIGNMENTS, {
    entityName: source,
  })
}

/**
 * Track a Goal step option being toggled/changed, with the option (and, where the option itself
 * is the chosen value, e.g. goal type or approach) in entityName.
 */
export function trackGoalOptionToggled(entityName: string): void {
  trackEvent(APPLICATIONS_EVENT_NAMES.ASSIGNMENT_GOAL_OPTION_TOGGLED, APPLICATIONS_SUB_FEATURE_IDS.REVIEW_ASSIGNMENTS, {
    entityName,
  })
}

/**
 * Track a condition added to or removed from an assignment's Conditions step, with the filter
 * option's name in entityName (e.g. TICKET_TAGS).
 */
export function trackAssignmentConditions(action: 'added' | 'removed', optionName: string): void {
  const eventName =
    action === 'added' ? APPLICATIONS_EVENT_NAMES.ASSIGNMENT_CONDITION_ADDED : APPLICATIONS_EVENT_NAMES.ASSIGNMENT_CONDITION_REMOVED

  trackEvent(eventName, APPLICATIONS_SUB_FEATURE_IDS.REVIEW_ASSIGNMENTS, { entityName: optionName })
}

/**
 * Track the ReviewerQA evaluation modal being closed (cancel button or the modal's own x),
 * distinguishing whether an evaluation had already been submitted at that point.
 */
export function trackReviewerQaEvaluationClosed(hasSubmittedEvaluation: boolean): void {
  const eventName = hasSubmittedEvaluation
    ? APPLICATIONS_EVENT_NAMES.REVIEWERQA_EVALUATION_CLOSED_AFTER_EVAL
    : APPLICATIONS_EVENT_NAMES.REVIEWERQA_EVALUATION_CANCELLED

  trackEvent(eventName, APPLICATIONS_SUB_FEATURE_IDS.REVIEW_ASSIGNMENTS)
}

/**
 * Stable context label for the shared filter component, so filter events can be compared across
 * the assignments-creation flow and the conversations page. Passed as `entityName`.
 */
export function filterContextLabel(modalType?: FilterModalType): string {
  switch (modalType) {
    case FilterModalType.ReviewerQA:
      return 'assignments'
    case FilterModalType.PromptTesting:
      return 'prompt-testing'
    default:
      return 'conversations'
  }
}
